import type { FunctionComponent, SVGProps } from 'react';

import ArrowLeftIcon from '@/assets/icons/ArrowLeft.svg?react';
import ArrowRightIcon from '@/assets/icons/ArrowRight.svg?react';
import AppsIcon from '@/assets/icons/Apps.svg?react';
import BackspaceIcon from '@/assets/icons/Backspace.svg?react';
import CancelIcon from '@/assets/icons/Cancel.svg?react';
import CloseIcon from '@/assets/icons/Close.svg?react';
import CompasIcon from '@/assets/icons/Compas.svg?react';
import ExitIcon from '@/assets/icons/Exit.svg?react';
import KeyboardIcon from '@/assets/icons/Keyboard.svg?react';
import LockIcon from '@/assets/icons/Lock.svg?react';
import MicIcon from '@/assets/icons/Mic.svg?react';
import NeovexPoweredLogoIcon from '@/assets/icons/NeovexPoweredLogo.svg?react';
import PersonLockIcon from '@/assets/icons/PersonLock.svg?react';
import SendMessageIcon from '@/assets/icons/SendMessage.svg?react';
import SettingsIcon from '@/assets/icons/Settings.svg?react';
import TranslateIcon from '@/assets/icons/Translate.svg?react';

import arrowLeftUrl from '@/assets/icons/ArrowLeft.svg';
import arrowRightUrl from '@/assets/icons/ArrowRight.svg';
import appsUrl from '@/assets/icons/Apps.svg';
import backspaceUrl from '@/assets/icons/Backspace.svg';
import cancelUrl from '@/assets/icons/Cancel.svg';
import closeUrl from '@/assets/icons/Close.svg';
import compasUrl from '@/assets/icons/Compas.svg';
import exitUrl from '@/assets/icons/Exit.svg';
import keyboardUrl from '@/assets/icons/Keyboard.svg';
import lockUrl from '@/assets/icons/Lock.svg';
import micUrl from '@/assets/icons/Mic.svg';
import neovexPoweredLogoUrl from '@/assets/icons/NeovexPoweredLogo.svg';
import personLockUrl from '@/assets/icons/PersonLock.svg';
import sendMessageUrl from '@/assets/icons/SendMessage.svg';
import settingsUrl from '@/assets/icons/Settings.svg';
import translateUrl from '@/assets/icons/Translate.svg';

import ArIcon from '@/assets/icons/language_switch/Ar.svg?react';
import BottomCornerIcon from '@/assets/icons/language_switch/BottomCorner.svg?react';
import EnIcon from '@/assets/icons/language_switch/En.svg?react';
import RuIcon from '@/assets/icons/language_switch/Ru.svg?react';
import SquareIcon from '@/assets/icons/language_switch/Square.svg?react';
import TopCornerIcon from '@/assets/icons/language_switch/TopCorner.svg?react';
import UzIcon from '@/assets/icons/language_switch/Uz.svg?react';

import arUrl from '@/assets/icons/language_switch/Ar.svg';
import bottomCornerUrl from '@/assets/icons/language_switch/BottomCorner.svg';
import enUrl from '@/assets/icons/language_switch/En.svg';
import ruUrl from '@/assets/icons/language_switch/Ru.svg';
import squareUrl from '@/assets/icons/language_switch/Square.svg';
import topCornerUrl from '@/assets/icons/language_switch/TopCorner.svg';
import uzUrl from '@/assets/icons/language_switch/Uz.svg';

/**
 * Перенос `lib/src/config/app_icons.dart` и
 * `lib/src/presentation/widgets/language_switch_animation/language_switch_icons.dart`.
 *
 * В Dart здесь лежали строки-пути (`assets/icons/Mic.svg`), которые
 * `SvgAsset` читал из бандла ассетов. В вебе путь принадлежит сборщику:
 * Vite сам проставляет хеш в имя файла, поэтому константу `path` из Dart
 * переносить некуда — она удалена.
 *
 * Каждая иконка экспортируется дважды, потому что вызывающий код нуждается
 * то в одном, то в другом:
 *  - [AppIconComponents] — React-компонент (`?react`, vite-plugin-svgr).
 *    Нужен там, где цвет задаётся через `currentColor`/CSS.
 *  - [AppIcons] — строка URL. Нужна для `<img src>` и фоновых картинок.
 *
 * Имена ключей совпадают с геттерами Dart один в один.
 */

/** Тип React-компонента, который отдаёт `vite-plugin-svgr` по суффиксу `?react`. */
export type SvgIconComponent = FunctionComponent<
  SVGProps<SVGSVGElement> & { title?: string }
>;

export const AppIcons = {
  sendMessage: sendMessageUrl,
  close: closeUrl,
  mic: micUrl,
  translate: translateUrl,
  keyboard: keyboardUrl,
  arrowRight: arrowRightUrl,
  arrowLeft: arrowLeftUrl,
  cancel: cancelUrl,
  compas: compasUrl,
  backspace: backspaceUrl,
  lock: lockUrl,
  exit: exitUrl,
  apps: appsUrl,
  neovexPoweredLogo: neovexPoweredLogoUrl,
  settings: settingsUrl,
  personLock: personLockUrl,
} as const;

export type AppIconName = keyof typeof AppIcons;

/** Тот же набор имён, что и в [AppIcons], но React-компонентами. */
export const AppIconComponents: Readonly<Record<AppIconName, SvgIconComponent>> =
  {
    sendMessage: SendMessageIcon,
    close: CloseIcon,
    mic: MicIcon,
    translate: TranslateIcon,
    keyboard: KeyboardIcon,
    arrowRight: ArrowRightIcon,
    arrowLeft: ArrowLeftIcon,
    cancel: CancelIcon,
    compas: CompasIcon,
    backspace: BackspaceIcon,
    lock: LockIcon,
    exit: ExitIcon,
    apps: AppsIcon,
    neovexPoweredLogo: NeovexPoweredLogoIcon,
    settings: SettingsIcon,
    personLock: PersonLockIcon,
  };

export const LanguageSwitchIcons = {
  ar: arUrl,
  en: enUrl,
  ru: ruUrl,
  uz: uzUrl,
  topCorner: topCornerUrl,
  bottomCorner: bottomCornerUrl,
  square: squareUrl,
} as const;

export type LanguageSwitchIconName = keyof typeof LanguageSwitchIcons;

/** Тот же набор имён, что и в [LanguageSwitchIcons], но React-компонентами. */
export const LanguageSwitchIconComponents: Readonly<
  Record<LanguageSwitchIconName, SvgIconComponent>
> = {
  ar: ArIcon,
  en: EnIcon,
  ru: RuIcon,
  uz: UzIcon,
  topCorner: TopCornerIcon,
  bottomCorner: BottomCornerIcon,
  square: SquareIcon,
};
